import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';

const Privacy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen pt-4 pb-36 md:pt-24 md:pb-8 px-4" data-testid="privacy-page">
      <SEO path="/privacy" />
      <div className="max-w-3xl mx-auto">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-6 gap-2">
          <ArrowLeft className="w-4 h-4" /> Terug
        </Button>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="font-display text-4xl md:text-5xl text-foreground mb-3">Privacybeleid</h1>
          <p className="text-muted-foreground mb-10">Laatst bijgewerkt: 19 februari 2026 · Versie 2.0</p>

          <div className="prose prose-lg max-w-none text-foreground space-y-8">
            <section className="space-y-3">
              <h2 className="font-display text-2xl text-foreground">1. Verwerkingsverantwoordelijke</h2>
              <p className="text-muted-foreground leading-relaxed">
                Oma's Verhaaltjes is een digitaal platform voor prentenboeken. De verantwoordelijke voor
                de verwerking van persoonsgegevens is:
              </p>
              <div className="bg-muted/40 p-4 rounded-xl text-sm text-muted-foreground space-y-1">
                <p><strong className="text-foreground">Oma's Verhaaltjes</strong></p>
                <p>[INVULLEN: rechtsvorm en bedrijfsnaam, bv. Oma's Verhaaltjes B.V.]</p>
                <p>[INVULLEN: bezoekadres, postcode en plaats]</p>
                <p>KvK: [INVULLEN: KvK-nummer]</p>
                <p>BTW: [INVULLEN: BTW-identificatienummer]</p>
                <p>E-mail: <a className="text-gold hover:underline" href="mailto:privacy@omasverhaaltjes.nl">privacy@omasverhaaltjes.nl</a></p>
              </div>
            </section>

            <section className="space-y-3">
              <h2 className="font-display text-2xl text-foreground">2. Welke gegevens verzamelen wij?</h2>
              <p className="text-muted-foreground leading-relaxed">
                Wij verzamelen uitsluitend gegevens die noodzakelijk zijn voor de dienstverlening. Per
                categorie staan het doel, de wettelijke grondslag (AVG art. 6) en de bewaartermijn vermeld.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left border-collapse">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="py-2 pr-4 text-foreground font-semibold">Gegeven</th>
                      <th className="py-2 pr-4 text-foreground font-semibold">Doel</th>
                      <th className="py-2 pr-4 text-foreground font-semibold">Grondslag</th>
                      <th className="py-2 text-foreground font-semibold">Bewaartermijn</th>
                    </tr>
                  </thead>
                  <tbody className="text-muted-foreground">
                    <tr className="border-b border-border">
                      <td className="py-2 pr-4">E-mailadres, naam, wachtwoord (versleuteld)</td>
                      <td className="py-2 pr-4">Account aanmaken en beheren</td>
                      <td className="py-2 pr-4">Overeenkomst (art. 6.1.b)</td>
                      <td className="py-2">Zolang je account bestaat + 3 maanden</td>
                    </tr>
                    <tr className="border-b border-border">
                      <td className="py-2 pr-4">Leesvoortgang, favorieten, leesreeksen</td>
                      <td className="py-2 pr-4">Gepersonaliseerde leeservaring</td>
                      <td className="py-2 pr-4">Overeenkomst (art. 6.1.b)</td>
                      <td className="py-2">Zolang je account bestaat</td>
                    </tr>
                    <tr className="border-b border-border">
                      <td className="py-2 pr-4">Betaalgegevens (verwerkt door Mollie, wij zien enkel status)</td>
                      <td className="py-2 pr-4">Afhandeling abonnement en cadeaucodes</td>
                      <td className="py-2 pr-4">Overeenkomst + wettelijke plicht</td>
                      <td className="py-2">7 jaar (fiscale bewaarplicht)</td>
                    </tr>
                    <tr className="border-b border-border">
                      <td className="py-2 pr-4">IP-adres, browser, apparaattype</td>
                      <td className="py-2 pr-4">Beveiliging, foutdiagnose</td>
                      <td className="py-2 pr-4">Gerechtvaardigd belang (art. 6.1.f)</td>
                      <td className="py-2">Maximaal 30 dagen in logs</td>
                    </tr>
                    <tr className="border-b border-border">
                      <td className="py-2 pr-4">Door jou geüploade verhalen en illustraties</td>
                      <td className="py-2 pr-4">Publicatie op platform (na goedkeuring)</td>
                      <td className="py-2 pr-4">Overeenkomst (art. 6.1.b)</td>
                      <td className="py-2">Zolang publicatie gewenst is</td>
                    </tr>
                    <tr>
                      <td className="py-2 pr-4">Ingestelde voorkeuren (taal, bedtijdmodus)</td>
                      <td className="py-2 pr-4">Voorkeuren bewaren</td>
                      <td className="py-2 pr-4">Overeenkomst (art. 6.1.b)</td>
                      <td className="py-2">Tot je ze zelf wijzigt</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-muted-foreground leading-relaxed text-sm">
                Wij verzamelen <strong>geen</strong> precieze locatiegegevens, geen advertising-IDs, en wij
                doen <strong>geen tracking over apps of websites heen</strong>. Er worden geen third-party
                advertentienetwerken gebruikt.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="font-display text-2xl text-foreground">3. Cookies en lokale opslag</h2>
              <p className="text-muted-foreground leading-relaxed">
                Wij plaatsen uitsluitend <strong>functionele cookies / lokale opslag</strong> voor het
                ingelogd houden van gebruikers (JWT-token, 24 uur geldig), het onthouden van voorkeuren
                (taal, bedtijdmodus) en het cachen van PWA-assets voor offline gebruik. Hiervoor is geen
                toestemming vereist (AVG overweging 30). Wij gebruiken <strong>geen</strong> analytische
                of advertentiecookies.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="font-display text-2xl text-foreground">4. Subverwerkers en doorgifte</h2>
              <p className="text-muted-foreground leading-relaxed">
                Wij schakelen de volgende zorgvuldig geselecteerde verwerkers in, met wie een
                verwerkersovereenkomst (AVG art. 28) is afgesloten:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li><strong className="text-foreground">Mollie B.V.</strong> (Amsterdam, NL) — betalingsverwerking. Zie <a className="text-gold hover:underline" href="https://www.mollie.com/privacy" target="_blank" rel="noopener noreferrer">mollie.com/privacy</a>.</li>
                <li><strong className="text-foreground">Resend, Inc.</strong> (Verenigde Staten, onder EU-US Data Privacy Framework) — verzending van transactionele e-mails. Zie <a className="text-gold hover:underline" href="https://resend.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer">resend.com/privacy</a>.</li>
                <li><strong className="text-foreground">[INVULLEN: Hosting / cloudprovider, bv. Hetzner (DE) of AWS (EU-west)]</strong> — hosting en database opslag binnen de EU.</li>
                <li><strong className="text-foreground">OpenAI (via Emergent LLM)</strong> — uitsluitend voor het genereren van verhaal-illustraties; er worden hierbij geen persoonsgegevens verstuurd.</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed">
                Doorgifte naar derde landen gebeurt uitsluitend onder passende waarborgen (EU
                modelcontractbepalingen of een geldig adequaatheidsbesluit).
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="font-display text-2xl text-foreground">5. Bescherming van kinderen</h2>
              <p className="text-muted-foreground leading-relaxed">
                Onze dienst is bedoeld voor <strong>(groot)ouders</strong> die verhalen voorlezen aan
                kinderen van 3 tot 8 jaar. Accounts mogen uitsluitend worden aangemaakt door personen
                van <strong>16 jaar of ouder</strong> (AVG art. 8). Wij verzamelen bewust geen
                persoonsgegevens van kinderen zelf.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                Mocht je als ouder vermoeden dat je kind zelf een account heeft aangemaakt met onjuiste
                leeftijdsopgave, neem dan contact met ons op via <a className="text-gold hover:underline" href="mailto:privacy@omasverhaaltjes.nl">privacy@omasverhaaltjes.nl</a>. Wij verwijderen
                dergelijke accounts onmiddellijk.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="font-display text-2xl text-foreground">6. Beveiliging</h2>
              <p className="text-muted-foreground leading-relaxed">
                Wij nemen passende technische en organisatorische maatregelen, waaronder: TLS-versleuteling
                voor alle verbindingen, wachtwoord-hashing met bcrypt, JWT-authenticatie met korte
                geldigheidsduur, role-based access control, beveiligde backups, en regelmatige
                veiligheidsupdates van afhankelijkheden.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="font-display text-2xl text-foreground">7. Jouw rechten</h2>
              <p className="text-muted-foreground leading-relaxed">
                Onder de AVG heb je de volgende rechten:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                <li><strong>Inzage</strong> — een kopie van je gegevens opvragen</li>
                <li><strong>Rectificatie</strong> — onjuiste gegevens laten corrigeren</li>
                <li><strong>Wissen</strong> ("recht om vergeten te worden") — je account en bijbehorende data verwijderen</li>
                <li><strong>Beperking</strong> — gebruik van je gegevens tijdelijk stilleggen</li>
                <li><strong>Dataportabiliteit</strong> — je gegevens ontvangen in een gangbaar formaat</li>
                <li><strong>Bezwaar</strong> — tegen verwerking op basis van gerechtvaardigd belang</li>
                <li><strong>Toestemming intrekken</strong> — waar we op toestemming berusten</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed">
                Verzoeken kun je indienen via <a className="text-gold hover:underline" href="mailto:privacy@omasverhaaltjes.nl">privacy@omasverhaaltjes.nl</a>. Wij reageren binnen 30 dagen.
                Ter verificatie vragen wij mogelijk een identiteitsbewijs (andere gegevens afgeschermd).
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="font-display text-2xl text-foreground">8. Klachtrecht</h2>
              <p className="text-muted-foreground leading-relaxed">
                Ben je niet tevreden over hoe wij omgaan met jouw gegevens? Dan kun je een klacht
                indienen bij de Autoriteit Persoonsgegevens via <a className="text-gold hover:underline" href="https://www.autoriteitpersoonsgegevens.nl" target="_blank" rel="noopener noreferrer">autoriteitpersoonsgegevens.nl</a>.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="font-display text-2xl text-foreground">9. Wijzigingen</h2>
              <p className="text-muted-foreground leading-relaxed">
                Wij kunnen dit privacybeleid wijzigen wanneer wetgeving of dienstverlening verandert.
                Bij ingrijpende wijzigingen informeren we ingelogde gebruikers per e-mail. De meest recente
                versie vind je altijd op deze pagina. Doorgaan met gebruik na wijziging betekent dat je
                de nieuwe voorwaarden accepteert.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="font-display text-2xl text-foreground">10. Contact</h2>
              <p className="text-muted-foreground leading-relaxed">
                Voor alle privacy-gerelateerde vragen: <a className="text-gold hover:underline" href="mailto:privacy@omasverhaaltjes.nl">privacy@omasverhaaltjes.nl</a><br />
                Voor algemene vragen: <a className="text-gold hover:underline" href="mailto:info@omasverhaaltjes.nl">info@omasverhaaltjes.nl</a>
              </p>
            </section>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Privacy;
